/* eslint-disable camelcase */
import { Button, ButtonLink, Spinner, Stack, Tooltip, TooltipContent, TooltipTrigger } from 'component-library';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import {
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS,
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE,
  IndicatorProductSubscriptionConfiguration,
} from '@/api/rest/resources/types/IndicatorProduct';
import { SuspenseBoundary } from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { Card } from '@/components/widgets/o/Card/Card';
import {
  useGetIndicatorProductSubscriptionConfigurations,
  useUpdateIndicatorProductSubscriptionConfiguration,
} from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { usePathSet } from '@/hooks/usePathSet';
import { Logger } from '@/lib/logs/logger';
import { buildPath } from '@/utils/buildPath';

import { AddNewConfig } from './AddNewConfig';
import { BUTTON_STYLES, DeleteInSituConfigurationWithConfirmation, StatusPill } from './InSituDataCards';

export const MediaMapCards = () => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card
          styles={{
            root: 'rounded-3xl p-4',
          }}
        >
          <Stack spacing={4}>
            <span className='typography-body1'>{t('shared.mediaMaps.newConfig.labels.mediaMapUpload')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`Media map data upload rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary
        fallback={
          <Stack centerMain>
            <Spinner size={10} />
          </Stack>
        }
      >
        <MediaMapCardsBase />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const MediaMapCardsBase = () => {
  const configurations =
    useGetIndicatorProductSubscriptionConfigurations(undefined, true, {
      type: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.MEDIA_MAP,
    }).data?.results ?? [];
  const { t } = useTranslation();

  const hasNoData = configurations.length === 0;
  const sortedConfigurations = configurations.sort((a, b) => a.indicator_name.localeCompare(b.indicator_name));

  return (
    <Stack spacing={4} className='gap-4 justify-start flex-wrap' direction={hasNoData ? 'col' : 'row'}>
      {hasNoData && <span>{t('shared.mediaMaps.newConfig.labels.noData')}</span>}
      {sortedConfigurations.map((config, i) => (
        <MediaMapCard key={`media-map-config-${i}`} config={config} />
      ))}
      <AddNewConfig type={INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.MEDIA_MAP} />
    </Stack>
  );
};

const MediaMapCard = ({ config }: { config: IndicatorProductSubscriptionConfiguration }) => {
  const { t } = useTranslation();
  const isStaffUser = useIsStaffUser();
  const { submit: updateSubmit, isSubmitting: isUpdating } = useUpdateIndicatorProductSubscriptionConfiguration();

  const pathSet = usePathSet();
  const { indicator_name, status, id } = config;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const handleStatusUpdate = async (
    newStatus: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS],
  ) => {
    await updateSubmit(id, { status: newStatus });
  };

  const linkPath = buildPath(pathSet.mediaMapsConfiguration, {
    pathParams: { configurationId: id },
  });

  return (
    <>
      <Stack spacing={2} center centerMain>
        <ButtonLink
          data-testid='config-card-button'
          className='rounded-3xl border border-neutral-black-12 p-6 transition-all duration-200 gap-2 w-[380px] h-[320px] hover:border-primary-hover hover:bg-neutral-hover text-neutral-black-40'
          variant='text'
          to={linkPath}
        >
          <Stack spacing={4} center centerMain>
            <Stack center centerMain>
              <Icon icon='ri-upload-cloud-line' className='text-7xl' />
            </Stack>

            <Stack spacing={2} center centerMain>
              <span className='typography-h4 whitespace-pre-wrap text-neutral-black-80'>{indicator_name}</span>
              <StatusPill status={status} />
            </Stack>
          </Stack>
        </ButtonLink>
        <Stack direction={isStaffUser ? 'row' : 'col'} spacing={2} className='justify-start w-full'>
          {isStaffUser && (
            <AddNewConfig
              asIcon={true}
              insituIpSubscriptionIdArg={id}
              type={INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.MEDIA_MAP}
            />
          )}
          {isStaffUser &&
            (
              [
                INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.REJECTED,
                INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.VALIDATED,
              ] as const
            ).includes(status as 'rejected' | 'validated') && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid='config-card-show-button'
                    onClick={() => handleStatusUpdate(INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.APPROVED)}
                    disabled={isUpdating}
                    className={BUTTON_STYLES}
                  >
                    <Icon icon='ri-eye-line' className='text-md' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className='whitespace-pre-wrap'>
                  <Stack className='gap-4'>{t('shared.insituDataUpload.newConfig.button.show')}</Stack>
                </TooltipContent>
              </Tooltip>
            )}
          {isStaffUser && status === INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.APPROVED && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid='config-card-hide-button'
                  onClick={() => handleStatusUpdate(INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.REJECTED)}
                  disabled={isUpdating}
                  className={BUTTON_STYLES}
                >
                  <Icon icon='ri-eye-off-line' className='text-md' />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='whitespace-pre-wrap'>
                <Stack className='gap-4'>{t('shared.insituDataUpload.newConfig.button.hide')}</Stack>
              </TooltipContent>
            </Tooltip>
          )}
          {isStaffUser && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid='config-card-delete-button'
                  onClick={() => setShowDeleteConfirmation(true)}
                  className={`bg-error hover:bg-error-dark ${BUTTON_STYLES}`}
                >
                  <Icon icon='ri-delete-bin-line' className='text-md' />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='whitespace-pre-wrap'>
                <Stack className='gap-4'>{t('global.ui.buttons.delete')}</Stack>
              </TooltipContent>
            </Tooltip>
          )}
        </Stack>
        <DeleteInSituConfigurationWithConfirmation
          config={showDeleteConfirmation ? config : null}
          onClose={() => setShowDeleteConfirmation(false)}
        />
      </Stack>
    </>
  );
};
